// Copyright © 2025 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, Fragment } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { get, isUndefined } from 'lodash';
import { push } from 'connected-react-router';

import {
  Footer,
  StatusIndicator,
  TabsContainer,
  TabPanel,
} from '../../../components';
import { getTabIndex } from '../../../utils';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import { isCompatibleBundleVersion } from '../../../utils/datetime';

import styles from './styles';

import {
  fetchProxy,
  fetchProxyDetails,
  fetchProxyGateway,
  deleteProxy,
  fetchApiKeysCount,
  fetchProxyApiKeys,
  fetchApis,
  fetchProxyApis,
  fetchProxyApiPlans,
  fetchProxyOrganizations,
  fetchRateLimitQuota,
  fetchProxyDeploymentStatus,
  updateNewProxySync,
  updateProxy,
  deployRateLimits,
} from '../../../actions/proxy';
import {
  getProxyDetails,
  getProxy,
  getProxyGatewayDetailsLoading,
  getParsedProxyGatewayDetails,
  getProxyGateways,
  getIsProxyDeleteSuccess,
  getHasError,
  getPageErrors,
  getIsLoading,
  getProxyUpdateStatus,
} from '../../../reducers/proxy';
import { fetchKeyStore } from '../../../actions/keyStore';
import {
  fetchDeploymentRefreshTime,
} from '../../../actions/portalConfig';
import {
  getConfig,
  getDeploymentRefreshTime,
  getFeatureFlags,
  getIsPortalAdmin,
} from '../../../reducers/portalConfig';
import { DetailsHeader } from '../../../components';
import DetailsContainer from '../../details';
import Overview from './Overview';
import Apis from './Apis';
import Keys from './Keys';
import Certificates from './Certificates';
import EntitiesSummary from './EntitiesSummary';
import Actions from './Actions';
import PendingEnrollment from './PendingEnrollment';
import ApiLocation from "./ApiLocation";
import {
  ALERT_SUCCESS,
  ALERT_ERROR,
  PROXY_ENROLMENT_STATUS_PENDING,
  PROXY_CONNECTED,
  PROXY_DISCONNECTED,
  SUCCESS,
  FAIL,
} from '../../../constants';
import { withStyles } from "@material-ui/core";

export const getProxyUuid = (props) => get(props, 'match.path')
  && get(props, 'match.path').includes('/details/')
  && get(props, 'match.params.apiProxyUuid');

export const getProxyStatus = (proxyDetails) => {
  const mqConnected = get(proxyDetails, 'messageQueueDetails.connected');
  if(isUndefined(mqConnected)) { return null; }
  return mqConnected ? PROXY_CONNECTED : PROXY_DISCONNECTED;
};

const getTabItems = (featureFlagL7Manager) => [
  {
    id: 'overview',
    tabId: 'proxy-overview-tab',
    label: getI18nFormattedMessage('label.proxy.details.page.overview.title'),
    visible: true,
  },
  {
    id: 'apis',
    tabId: 'proxy-apis-tab',
    label: getI18nFormattedMessage('label.proxy.details.page.apis.deployments.title'),
    visible: true,
  },
  {
    id: 'keys',
    tabId: 'proxy-keys-tab',
    label: getI18nFormattedMessage('label.proxy.details.page.application.api.keys.deployments.title'),
    visible: true,
  },
  {
    id: 'api-location',
    tabId: 'proxy-api-location-tab',
    label: getI18nFormattedMessage('label.api.location.tab'),
    visible: true,
  },
  {
    id: 'certificates',
    tabId: 'proxy-certificates-tab',
    label: getI18nFormattedMessage('label.certificates'),
    visible: featureFlagL7Manager,
  },
  {
    id: 'entities',
    tabId: 'proxy-entities-summary-tab',
    label: getI18nFormattedMessage('label.entities.summary'),
    visible: featureFlagL7Manager,
  },
  // {
  //   id: 'organizations',
  //   tabId: 'proxy-organizations-tab',
  //   label: getI18nFormattedMessage('label.organizations'),
  //   visible: true,
  // },
];

const portPattern =
  /:([1-9][0-9]{0,3}|[1-5][0-9]{4}|6[0-4][0-9]{3}|65[0-4][0-9]{2}|655[0-2][0-9]|6553[0-5])$/i;

export const getIsCompatible = ({
  gatewayDetailsLoading,
  tenantGatewayLogs,
}) => {
  if(gatewayDetailsLoading) { return; }
  const bundleVersion = get(tenantGatewayLogs, 'bundle_version');
  return isCompatibleBundleVersion(bundleVersion);
};

export const ProxyDetails = (props) => {
  const {
    isLoading,
    featureFlags,
    proxyDetails,
    gatewayDetailsLoading,
    parsedGatewayDetails = {},
    hasError,
    pageErrors,
    proxy,
    proxyUpdateStatus,
    updateProxy,
    isPortalAdmin,
    history,
  } = props;

  const proxyUuid = get(props, 'match.params.apiProxyUuid');
  const featureFlagL7Manager = JSON.parse(get(featureFlags, 'l7Manager.value'));
  const tabItems = getTabItems(featureFlagL7Manager);

  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [errors, setErrors] = useState([]);
  const [currentTab, setCurrentTab] = useState(
    getTabIndex(tabItems, props.match),
  );

  const { enrollmentStatus, tenantGatewayLogs } = parsedGatewayDetails;
  const isCompatible = getIsCompatible({
    gatewayDetailsLoading,
    tenantGatewayLogs,
  });
  const isPendingEnrollment =
    (enrollmentStatus === PROXY_ENROLMENT_STATUS_PENDING);
  const proxyStatus = getProxyStatus(proxyDetails);

  useEffect(() => { if(!isPortalAdmin) { props.push('/404');} }, [isPortalAdmin]);

  useEffect(() => {
    props.fetchProxy(proxyUuid);
    props.fetchProxyGateway(proxyUuid);
    props.fetchRateLimitQuota(proxyUuid);
    props.fetchProxyDeploymentStatus(proxyUuid);
    props.fetchDeploymentRefreshTime();
  }, []);

  useEffect(() => {
    setCurrentTab(getTabIndex(tabItems, props.match));
  }, [props.match]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    setErrors(hasError ? pageErrors : []);
  }, [hasError, pageErrors]);

  const handleTabChange = (e, newValue) => {
    const tabs = tabItems.filter(({ visible }) => visible);
    setCurrentTab(newValue);
    history.push(`/publish/api-proxy/details/${proxyUuid}/${tabs[newValue].id}`)
  };

  const onSwitchNewSync = () =>{
    props.updateNewProxySync(proxyUuid);
  };

  const extractHostAndPort = (proxy) => {
    const clusterNameOverride = get(proxy, 'clusterNameOverride', '');
    const defaultProxyUrl = get(proxy, 'proxyUrl', '');
    const url = clusterNameOverride ? clusterNameOverride : defaultProxyUrl;
    let host = '';
    let port = '';
    const portMatches = url.match(portPattern);
    if (portMatches) {
      [, port] = portMatches;
      host = url.replace(portPattern, '');
    }	else {
      host = url;
    }
    return { host, port };
  };

  const { host: clusterName, port: clusterPort } = extractHostAndPort(proxy);
  const [updatedProxyUrl, setUpdatedProxyUrl] = useState('');
  const [updatedProxyPort, setUpdatedProxyPort] = useState('');

  useEffect(() => {
    setUpdatedProxyUrl(clusterName);
    setUpdatedProxyPort(clusterPort);
  }, [clusterName, clusterPort]);

  useEffect(() => {
    if (proxyUpdateStatus === SUCCESS) {
      notifyMessages(
        ALERT_SUCCESS,
        intl.getI18nMessage('label.proxy.details.page.update.success.message'),
      );
      props.fetchProxyGateway(proxyUuid);
      props.fetchProxy(proxyUuid);
      setTimeout(() => {
        notifyMessages('', '');
      }, 3000);
    } else if (proxyUpdateStatus === FAIL) {
      notifyMessages(
        ALERT_ERROR,
        intl.getI18nMessage('label.proxy.details.page.update.fail.message'),
      );
    }
  }, [proxyUpdateStatus]);

  const handleUrlChange = ({ host, portNumber }) => {
    const clusterName = portNumber ? `${host}:${portNumber}` : host;
    updateProxy({
      proxyUuid: proxyUuid,
      data: {
        name: get(proxyDetails, 'name'),
        clusterNameOverride: clusterName,
      },
    });
  };

  const Status = () =>
    <StatusIndicator
      status={isPendingEnrollment ? enrollmentStatus : proxyStatus}
    />;

  return (
    <Fragment>
      <DetailsContainer
        detailsPageId="proxy-details-page"
        isLoading={isLoading}
        notificationId="proxy-details-notifications"
        notificationStatus={notificationStatus}
        setNotificationStatus={setNotificationMessage}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        infoMessage={intl.getI18nMessage('label.proxy.details.info.switch.newSync')}
        infoLinkText={intl.getI18nMessage('label.proxy.details.info.switch.linkText')}
        handleInfoClick={onSwitchNewSync}
        infoSeverity="info"
        infoId="proxy-details-info"
        errors={errors}
        header={
          <DetailsHeader
            headerId="proxy-details-header-container"
            title={intl.getI18nMessage('label.proxy.details.page.title')}
            name={get(proxyDetails, 'name')}
            statusComponent={<Status />}
          />
        }
        actions={
          <Actions
            {...props}
            proxyUuid={proxyUuid}
            enrollmentStatus={enrollmentStatus}
            notifyMessages={notifyMessages}
          />
        }
        pending={(enrollmentStatus && isPendingEnrollment) && (
          <PendingEnrollment
            {...props}
            proxy={proxy}
            proxyUuid={proxyUuid}
            notifyMessages={notifyMessages}
          />
        )}
        tabs={
          <TabsContainer
            id={'proxy-details-tab-container'}
            tabValue={currentTab}
            orientation="horizontal"
            tabItems={tabItems.filter(({ visible }) => visible)}
            handleTabChange={handleTabChange}
          />
        }
        page={
          <Fragment>
            <TabPanel
              visible={currentTab === 0}
              id="proxy-overview-tab-panel"
            >
              <Overview
                {...props}
                isCurrentTab={currentTab === 0}
                proxyUuid={proxyUuid}
                proxyStatus={proxyStatus}
                parsedGatewayDetails={parsedGatewayDetails}
                isCompatible={isCompatible}
                notifyMessages={notifyMessages}
                handleUrlChange={handleUrlChange}
                proxyUrl={updatedProxyUrl}
                proxyPort={updatedProxyPort}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 1}
              id="proxy-apis-tab-panel"
            >
            <Apis
                {...props}
                isCurrentTab={currentTab === 1}
                gatewayBundleDetails={parsedGatewayDetails}
                proxyUuid={proxyUuid}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 2}
              id="proxy-keys-tab-panel"
            >
              <Keys
                {...props}
                isCurrentTab={currentTab === 2}
                gatewayBundleDetails={parsedGatewayDetails}
                proxyUuid={proxyUuid}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 3}
              id="proxy-api-location-tab"
            >
              <ApiLocation
                {...props}
                isCurrentTab={currentTab === 3}
                proxyUuid={proxyUuid}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 4}
              id="proxy-certificates-tab-panel"
            >
              <Certificates
                {...props}
                isCurrentTab={currentTab === 4}
                proxyUuid={proxyUuid}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
            <TabPanel
              visible={currentTab === 5}
              id="proxy-entities-summary-tab-panel"
            >
              <EntitiesSummary
                {...props}
                isCurrentTab={currentTab === 5}
                proxyUuid={proxyUuid}
                notifyMessages={notifyMessages}
              />
            </TabPanel>
          </Fragment>
        }
        footer={
          <Footer />
        }
      />
    </Fragment>
  );
};

const mapStateToProps = state => ({
  config: getConfig(state),
  featureFlags: getFeatureFlags(state),
  isLoading: getIsLoading(state),
  gatewayDetailsLoading: getProxyGatewayDetailsLoading(state),
  proxyDetails: getProxyDetails(state),
  proxy: getProxy(state),
  gateways: getProxyGateways(state),
  parsedGatewayDetails: getParsedProxyGatewayDetails(state),
  isProxyDeleteSuccess: getIsProxyDeleteSuccess(state),
  hasError: getHasError(state),
  pageErrors: getPageErrors(state),
  deploymentRefreshTime: getDeploymentRefreshTime(state),
  proxyUpdateStatus: getProxyUpdateStatus(state),
  isPortalAdmin: getIsPortalAdmin(state),
});

const mapDispatchToProps = {
  push,
  fetchProxy,
  fetchProxyDetails,
  fetchProxyGateway,
  deleteProxy,
  fetchApiKeysCount,
  fetchProxyApiKeys,
  fetchApis,
  fetchProxyApis,
  fetchProxyApiPlans,
  fetchProxyOrganizations,
  updateNewProxySync,
  updateProxy,
  fetchDeploymentRefreshTime,
  fetchRateLimitQuota,
  fetchProxyDeploymentStatus,
  deployRateLimits,
  fetchKeyStore,
};

ProxyDetails.propTypes = {
  config: shape({}),
  history: shape({}),
  proxyDetails: shape({}),
  parsedGatewayDetails: shape({}),
  gatewayDetailsLoading: bool,
  gatewayDetails: shape({}),
  featureFlags: shape({}),
  gateways: arrayOf(shape({})),
  isLoading: bool,
  hasError: bool,
  pageErrors: arrayOf(shape({})),
  match: shape({}),
  proxy: shape({}),
  proxyUpdateStatus: string,
  fetchProxy: func,
  fetchProxyGateway: func,
  fetchDeploymentRefreshTime: func,
  updateNewProxySync: func,
  updateProxy: func,
  fetchRateLimitQuota: func,
  fetchProxyDeploymentStatus: func,
  deployRateLimits: func,
  fetchKeyStore: func,
  isPortalAdmin: bool,
  push: func,
};
ProxyDetails.displayName = 'ProxyDetails';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ProxyDetails);
